import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import ServerHeader from './components/ServerHeader';
import MetricsTab from './components/MetricsTab';
import HealthTab from './components/HealthTab';
import ConfigEditorTab from './components/ConfigEditorTab';
import GovernorLogTab from './components/GovernorLogTab';
import Icon from '../../components/AppIcon';

const ServerDetail = () => {
  const [searchParams] = useSearchParams();
  const serverId = searchParams?.get('id') || 'srv-prod-01';
  const [activeTab, setActiveTab] = useState('metrics');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const serverData = {
    id: serverId,
    name: "Production Mail Server 01",
    ip: "203.0.113.45",
    location: "US East (Virginia)",
    status: "Online",
    uptime: "45d 12h",
    sentToday: 8547,
    deliverability: 98.7,
    reputation: 87,
    blacklistCount: 2
  };

  const metricsData = {
    reputationData: [
      { date: "02/17", score: 82 },
      { date: "02/18", score: 85 },
      { date: "02/19", score: 83 },
      { date: "02/20", score: 87 },
      { date: "02/21", score: 89 },
      { date: "02/22", score: 88 },
      { date: "02/23", score: 87 },
      { date: "02/24", score: 87 }
    ],
    dailyLimitData: [
      { date: "02/17", limit: 10000, sent: 8234 },
      { date: "02/18", limit: 10000, sent: 9156 },
      { date: "02/19", limit: 10000, sent: 7892 },
      { date: "02/20", limit: 10000, sent: 8547 },
      { date: "02/21", limit: 10000, sent: 9234 },
      { date: "02/22", limit: 10000, sent: 8765 },
      { date: "02/23", limit: 10000, sent: 8912 },
      { date: "02/24", limit: 10000, sent: 8547 }
    ],
    averageReputation: 86,
    totalSent: 69287,
    capacityUsed: 85
  };

  const healthData = {
    dnsChecks: [
      {
        type: "PTR Record",
        description: "Reverse DNS lookup",
        status: "Pass",
        value: "mail.example.com",
        message: "PTR record correctly configured and matches forward DNS"
      },
      {
        type: "SPF Record",
        description: "Sender Policy Framework",
        status: "Pass",
        value: "v=spf1 ip4:203.0.113.45 include:_spf.example.com ~all",
        message: "SPF record is valid and includes this server\'s IP"
      },
      {
        type: "DKIM Record",
        description: "DomainKeys Identified Mail",
        status: "Pass",
        value: "v=DKIM1; k=rsa; p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC...",
        message: "DKIM signature is valid and properly configured"
      },
      {
        type: "DMARC Record",
        description: "Domain-based Message Authentication",
        status: "Warning",
        value: "v=DMARC1; p=none; rua=mailto:dmarc@example.com",
        message: "DMARC policy is set to \'none\'. Consider upgrading to \'quarantine\' or \'reject'"
      }
    ],
    blacklists: [
      {
        id: "bl-001",
        name: "Spamhaus ZEN",
        description: "Combined blocking list from Spamhaus",
        listed: true,
        ip: "203.0.113.45",
        reason: "Detected spam-like behavior in recent campaigns",
        listedDate: "02/22/2026",
        delistUrl: "https://www.spamhaus.org/lookup/",
        recommendation: "Review recent email content and sending patterns. Ensure all recipients have opted in."
      },
      {
        id: "bl-002",
        name: "Barracuda Reputation",
        description: "Barracuda Networks IP reputation database",
        listed: true,
        ip: "203.0.113.45",
        reason: "High complaint rate detected",
        listedDate: "02/23/2026",
        delistUrl: "https://www.barracudacentral.org/rbl/removal-request",
        recommendation: "Implement double opt-in and review unsubscribe process to reduce complaints."
      }
    ],
    lastCheck: "2 minutes ago"
  };

  const governorLogs = [
    {
      id: "log-001",
      timestamp: new Date(Date.now() - 300000),
      severity: "Warning",
      type: "action",
      message: "Delivery rate dropped below threshold",
      action: "Reduced daily sending limit from 10,000 to 8,000",
      details: {
        "Previous Limit": "10,000",
        "New Limit": "8,000",
        "Trigger": "Deliverability < 95%",
        "Current Rate": "93.2%"
      },
      recommendation: "Monitor deliverability for next 24 hours. Consider reviewing email content and list quality."
    },
    {
      id: "log-002",
      timestamp: new Date(Date.now() - 3600000),
      severity: "Critical",
      type: "warning",
      message: "New blacklist detection",
      action: "Paused outbound email and initiated blacklist removal process",
      details: {
        "Blacklist": "Spamhaus ZEN",
        "Detection Time": "1 hour ago",
        "Action Taken": "Paused sending",
        "Status": "Removal requested"
      },
      recommendation: "Review recent campaigns for spam triggers. Verify all recipients opted in to receive emails."
    },
    {
      id: "log-003",
      timestamp: new Date(Date.now() - 7200000),
      severity: "Info",
      type: "info",
      message: "Reputation score improved",
      action: "Increased daily sending limit from 7,500 to 8,000",
      details: {
        "Previous Score": "82",
        "Current Score": "87",
        "Previous Limit": "7,500",
        "New Limit": "8,000"
      }
    },
    {
      id: "log-004",
      timestamp: new Date(Date.now() - 10800000),
      severity: "Success",
      type: "action",
      message: "Successful blacklist removal",
      action: "Resumed normal sending operations",
      details: {
        "Blacklist": "Barracuda Reputation",
        "Removal Time": "3 hours ago",
        "Downtime": "2.5 hours",
        "Status": "Fully operational"
      },
      recommendation: "Continue monitoring sending patterns and maintain current best practices."
    },
    {
      id: "log-005",
      timestamp: new Date(Date.now() - 14400000),
      severity: "Warning",
      type: "warning",
      message: "High bounce rate detected",
      action: "Initiated list cleaning process and reduced sending speed",
      details: {
        "Bounce Rate": "8.2%",
        "Threshold": "5%",
        "Emails Affected": "234",
        "Action": "Speed reduced by 30%"
      },
      recommendation: "Review and clean email list. Remove invalid addresses and implement email verification."
    },
    {
      id: "log-006",
      timestamp: new Date(Date.now() - 18000000),
      severity: "Info",
      type: "info",
      message: "Scheduled maintenance completed",
      action: "Updated mail server configuration and restarted services",
      details: {
        "Maintenance Type": "Configuration update",
        "Downtime": "5 minutes",
        "Services Restarted": "Postfix, Dovecot",
        "Status": "Successful"
      }
    }
  ];

  const tabs = [
    { id: 'metrics', label: 'Metrics', icon: 'TrendingUp' },
    { id: 'health', label: 'Health', icon: 'Shield' },
    { id: 'config', label: 'Config Editor', icon: 'FileCode' },
    { id: 'governor', label: "Governor\'s Log", icon: 'Activity' }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main 
        className={`pt-20 transition-smooth ${
          isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-[280px]'
        }`}
      >
        <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-4 md:mb-6">
            <Breadcrumbs />
          </div>

          <ServerHeader serverData={serverData} />

          <div className="bg-card border border-border rounded-lg overflow-hidden mb-6">
            <div className="border-b border-border overflow-x-auto scrollbar-thin">
              <div className="flex min-w-max">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-smooth border-b-2 whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 md:p-6 lg:p-8">
              {activeTab === 'metrics' && <MetricsTab metricsData={metricsData} />}
              {activeTab === 'health' && <HealthTab healthData={healthData} />}
              {activeTab === 'config' && <ConfigEditorTab serverId={serverId} />}
              {activeTab === 'governor' && <GovernorLogTab logs={governorLogs} />}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServerDetail;